/**
 * The normalized crafting data model.
 *
 * These types are the contract between `scripts/sync-manifest.mts` (which
 * produces them from Bungie's manifest) and everything downstream. They are
 * deliberately free of Bungie's raw shapes — nothing in the UI or the cost
 * engine should ever see a `reusablePlugSetHash`.
 */

/** Bumped whenever the normalized shape changes incompatibly. Saved and shared
 *  plans record it so we can detect a stale plan after a data migration. */
export const DATA_VERSION = 1;

export type CraftingSocketKey =
  | "intrinsic"
  | "barrel"
  | "magazine"
  | "traitOne"
  | "traitTwo"
  | "origin"
  | "memento";

/** Display order of the socket columns, matching the in-game slot bar. */
export const SOCKET_ORDER: readonly CraftingSocketKey[] = [
  "intrinsic",
  "barrel",
  "magazine",
  "traitOne",
  "traitTwo",
  "origin",
  "memento",
] as const;

export const SOCKET_LABELS: Record<CraftingSocketKey, string> = {
  intrinsic: "Intrinsic",
  barrel: "Barrel",
  magazine: "Magazine",
  traitOne: "Trait 1",
  traitTwo: "Trait 2",
  origin: "Origin Trait",
  memento: "Memento",
};

export interface ResourceAmount {
  itemHash: number;
  quantity: number;
}

export interface MaterialDefinition {
  itemHash: number;
  name: string;
  icon?: string;
}

export interface CraftingComponent {
  plugHash: number;
  name: string;
  description?: string;
  icon?: string;
  itemTypeDisplayName?: string;
  /** Manifest `inventory.tierType === 3`. Enhanced plugs are separate items
   *  sharing a display name with their base version — never dedupe by name. */
  enhanced: boolean;
  /** Weapon level at which this option becomes insertable. 0 = available now. */
  requiredLevel: number;
  costs: ResourceAmount[];
  /** False when the plug is no longer in the current loot pool. */
  currentlyCanRoll: boolean;
  /** statTypeHash -> modifier. Present only when the plug changes stats. */
  stats?: Record<number, number>;
}

export interface CraftingSocket {
  key: CraftingSocketKey;
  /** Index on the recipe item — kept for debugging against the raw manifest. */
  socketIndex: number;
  socketTypeHash: number;
  plugSetHash: number;
  options: CraftingComponent[];
}

export interface WeaponStat {
  statHash: number;
  name: string;
  value: number;
}

export interface CraftableWeapon {
  /** The recipe item (itemType 30) — this is what carries the crafting data. */
  recipeHash: number;
  /** The weapon the recipe outputs; source of icon, screenshot and stats. */
  itemHash: number;
  name: string;
  icon?: string;
  screenshot?: string;
  flavorText?: string;
  itemTypeDisplayName?: string;
  tierTypeName?: string;
  damageTypeHash?: number;
  damageTypeName?: string;
  /** Manifest icon path for the damage type — the real in-game element symbol. */
  damageTypeIcon?: string;
  ammoType?: number;
  stats: WeaponStat[];
  sockets: CraftingSocket[];
}

/** The light record used by the catalogue list — no plug data. */
export interface CraftableWeaponSummary {
  recipeHash: number;
  itemHash: number;
  name: string;
  icon?: string;
  itemTypeDisplayName?: string;
  damageTypeHash?: number;
  damageTypeName?: string;
  damageTypeIcon?: string;
  ammoType?: number;
  tierTypeName?: string;
}

export interface CraftablesIndex {
  dataVersion: number;
  manifestVersion: string;
  generatedAt: string;
  weaponCount: number;
  materials: Record<string, MaterialDefinition>;
  /** statHash -> name, for every stat a plug modifies. */
  statNames: Record<string, string>;
  weapons: CraftableWeaponSummary[];
}

/** A single weapon's committed fixture, as written by the sync script. */
export interface WeaponFixture {
  dataVersion: number;
  manifestVersion: string;
  materials: Record<string, MaterialDefinition>;
  statNames: Record<string, string>;
  weapon: CraftableWeapon;
}

// --- planner state ----------------------------------------------------------

/** Selected plug hash per column. Undefined = nothing chosen for that column. */
export type WeaponConfiguration = Partial<Record<CraftingSocketKey, number>>;

export type PlannerMode = "new-shape" | "owned-reshape";

export interface CraftingPlannerState {
  selectedWeaponHash?: number;
  selectedWeaponInstanceId?: string;
  currentWeaponLevel: number;
  /** 0–99.99; progress through the current level. */
  currentLevelPercent: number;
  /** The real weapon state from Bungie. Absent in "new-shape" mode. */
  currentConfiguration?: WeaponConfiguration;
  /** Local, unsaved preview. Never written back to Bungie. */
  plannedConfiguration: WeaponConfiguration;
  mode: PlannerMode;
}
